import { AiChatDocumentMessagePayloadRoleEnum } from '../../enums/AiChatDocumentMessagePayloadRoleEnum';
import type { AiChatDocumentMessagePayload } from '../../interfaces/AiChatDocumentMessagePayload';
import type { ChatStreamActivity } from '../../interfaces/ChatStreamActivity';

export type AiChatStreamPacket = {
  // snake_case + camelCase tolerance
  message_id?: number | string;
  messageId?: number | string;
  conversation_id?: string;
  conversationId?: string;
  stream_id?: string;
  streamId?: string;
  seq?: number | string;
  event?: string;
  tool?: string | null;
  phase?: string | null;
  params?: Record<string, unknown> | null;
  // Some backends nest `result_summary` under `params`.
  result_summary?: Record<string, unknown> | null;
  resultSummary?: Record<string, unknown> | null;
  text?: string | null;
  // Envelope mismatch (signalR sometimes forwards merged packet, but tolerate anyway)
  data?: unknown;
};

function sanitizeParams(rawParams: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!rawParams) return null;
  const paramsForState: Record<string, unknown> = { ...rawParams };
  if ('result_summary' in paramsForState) delete paramsForState.result_summary;
  if ('resultSummary' in paramsForState) delete paramsForState.resultSummary;
  return paramsForState;
}

function isPendingAssistant(message: AiChatDocumentMessagePayload): boolean {
  return (
    message.role === AiChatDocumentMessagePayloadRoleEnum.Assistant &&
    message.status === 'Pending' &&
    !message.isError
  );
}

/** Apply one ai_chat_stream packet. text replaces content; stale seq is dropped; done does not finalize. */
export function applyAiChatStreamPacket(
  messages: AiChatDocumentMessagePayload[],
  packet: AiChatStreamPacket,
): AiChatDocumentMessagePayload[] {
  const merged =
    packet && typeof packet === 'object' && packet.data && typeof packet.data === 'object'
      ? { ...(packet as Record<string, unknown>), ...(packet.data as Record<string, unknown>) }
      : packet;

  const messageId = Number(
    (merged as AiChatStreamPacket).message_id ?? (merged as AiChatStreamPacket).messageId,
  );
  const seq = Number((merged as AiChatStreamPacket).seq);
  if (!Number.isFinite(messageId) || !Number.isFinite(seq)) {
    return messages;
  }

  let changed = false;
  const next = messages.map((message) => {
    if (!isPendingAssistant(message)) {
      return message;
    }

    // Match by id, or adopt id onto the sole Pending assistant that has none yet
    // (document chat POST may only return guidChat).
    const idMatches = message.messageId === messageId;
    const adoptId = message.messageId == null;
    if (!idMatches && !adoptId) {
      return message;
    }

    const lastSeq = message.streamSeq ?? Number.NEGATIVE_INFINITY;
    if (seq <= lastSeq) {
      return message;
    }

    changed = true;
    const patched: AiChatDocumentMessagePayload = {
      ...message,
      streamSeq: seq,
      messageId: message.messageId ?? messageId,
      id: message.id ?? messageId,
    };

    const activityCarrier =
      (merged as { activity?: unknown }).activity &&
      typeof (merged as { activity?: unknown }).activity === 'object'
        ? ((merged as { activity: Record<string, unknown> }).activity as AiChatStreamPacket)
        : (merged as AiChatStreamPacket);
    const event = activityCarrier.event ?? (merged as AiChatStreamPacket).event;

    if (event === 'activity') {
      const previous = message.streamActivity;
      const history = message.streamActivities ? [...message.streamActivities] : [];

      const phase = activityCarrier.phase ?? null;
      const isStart = phase === 'start';
      const isDone = phase === 'done';

      const tool = activityCarrier.tool ?? previous?.tool ?? 'unknown';

      const rawParams = activityCarrier.params ?? null;
      const paramsObj = rawParams && typeof rawParams === 'object' ? rawParams : null;
      const embeddedResultSummary =
        (paramsObj as { result_summary?: unknown; resultSummary?: unknown } | null)
          ?.result_summary ??
        (paramsObj as { resultSummary?: unknown } | null)?.resultSummary ??
        null;
      const paramsForState = sanitizeParams(paramsObj);

      const resultSummaryRaw =
        (merged as AiChatStreamPacket).result_summary ??
        (merged as AiChatStreamPacket).resultSummary ??
        embeddedResultSummary ??
        null;

      let nextActivity: ChatStreamActivity;

      if (isDone) {
        let idx = -1;
        for (let i = history.length - 1; i >= 0; i -= 1) {
          const step = history[i];
          if (step && step.tool === tool && step.phase !== 'done') {
            idx = i;
            break;
          }
        }
        if (idx < 0 && history.length > 0) {
          idx = history.length - 1;
        }

        const existing = idx >= 0 ? history[idx] : undefined;
        if (existing) {
          nextActivity = {
            tool: existing.tool,
            phase: 'done',
            params: paramsForState ?? existing.params ?? null,
            resultSummary:
              (resultSummaryRaw as Record<string, unknown> | null) ??
              existing.resultSummary ??
              null,
          };
          history[idx] = nextActivity;
        } else {
          nextActivity = {
            tool,
            phase: 'done',
            params: paramsForState,
            resultSummary: resultSummaryRaw as Record<string, unknown> | null,
          };
          history.push(nextActivity);
        }
      } else {
        nextActivity = {
          tool,
          phase,
          params: isStart ? paramsForState : (paramsForState ?? previous?.params ?? null),
          resultSummary: (resultSummaryRaw as Record<string, unknown> | null) ?? null,
        };
        history.push(nextActivity);
      }

      patched.streamActivities = history;
      patched.streamActivity = nextActivity;
    }

    const streamEvent = String((merged as AiChatStreamPacket).event ?? '').toLowerCase();
    if (streamEvent === 'done' && patched.streamActivities?.length) {
      let closedAny = false;
      const closed = patched.streamActivities.map((activity) => {
        if (activity.phase === 'done') {
          return activity;
        }
        closedAny = true;
        return { ...activity, phase: 'done' as const };
      });
      if (closedAny) {
        patched.streamActivities = closed;
        patched.streamActivity = closed[closed.length - 1];
      }
    }

    if ((merged as AiChatStreamPacket).text != null) {
      patched.content = String((merged as AiChatStreamPacket).text);
    }

    return patched;
  });

  return changed ? next : messages;
}

/** Pull stream fields from top-level ai_chat_stream or nested data envelope. */
export function extractAiChatStreamPacket(event: {
  type?: string;
  data?: unknown;
  [key: string]: unknown;
}): AiChatStreamPacket | null {
  const data = event?.data as { type?: string } | undefined;
  const isStream = event?.type === 'ai_chat_stream' || data?.type === 'ai_chat_stream';
  if (!isStream) return null;

  if (event.type === 'ai_chat_stream') {
    const { data: notificationData, ...notificationRest } = event;
    return {
      ...(notificationData && typeof notificationData === 'object'
        ? (notificationData as object)
        : {}),
      ...notificationRest,
    } as AiChatStreamPacket;
  }

  const wrapped = data as Record<string, unknown> | undefined;
  const { data: wrappedData, ...wrappedRest } = wrapped ?? {};
  return {
    ...(wrappedData && typeof wrappedData === 'object' ? (wrappedData as object) : {}),
    ...wrappedRest,
  } as AiChatStreamPacket;
}
